import React, { useEffect, useState, useCallback, useContext } from 'react'
import { Box, Typography, Divider, Paper, Grid, Button, Avatar, Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import DateRangeIcon from '@mui/icons-material/DateRange';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import MobileDateRangePicker from '@mui/lab/MobileDateRangePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { startOfMonth } from 'date-fns'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { yellow, green, red, blue, teal, deepPurple, purple } from '@mui/material/colors';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';

import { AppContext } from 'core/context/';
import { getEarningsData } from 'services/earnings';
import { getFormattedDate, getDBDateFormat } from 'utils/common';
import LibrarySelect from 'components/librarySelect';

const Earnings = () => {

  const { enqueueSnackbar } = useSnackbar();
  const [librarySelected, setLibraySelected] = useState({});
  const [filterDates, setFilterDates] = React.useState([null, null]);
  const [toggleDateRangePicker, setToggleDateRangePicker] = useState(false);
  const [earningsData, setEarningsData] = useState(false);

  const { libraries } = useContext(AppContext);
  
  useEffect(() => {
    if(libraries?.length){
      setLibraySelected(libraries[0]);
      setFilterDates([startOfMonth(new Date()),  new Date()])
    }
  }, [libraries]);

  const onDateSelected = useCallback((dateRange) => {},[]);

  const onDateAccepted = useCallback(
    (dateRange) => {
      setFilterDates(dateRange);

  },[]);

  const fetchEarningsData = useCallback((library, filterDates) => {
    const [fromDate, toDate] = filterDates;
    const data = {
      propertyId: library?.libraryId,
      propertyType: "LIBRARY",
      fromDate: getDBDateFormat(fromDate),
      toDate: toDate ? getDBDateFormat(toDate) :  getDBDateFormat(fromDate)
    };

    getEarningsData({
      ...data,
    }).then(({response, error}) => {
      if(response){
        setEarningsData(response);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }, [enqueueSnackbar]);

  useEffect(() => {
    if(librarySelected?.libraryId && filterDates && filterDates[0] && filterDates[1]){
      fetchEarningsData(librarySelected, filterDates);
    }
  }, [librarySelected, filterDates, fetchEarningsData]);

  const onLibrarySelected = useCallback((lib)=> {
    setLibraySelected({...lib})
  },[]);

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Earnings
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container columnSpacing={4} rowSpacing={2} >
          <Grid item xs={12} sm={12} md={12} xl={12} lg={12} >
            <Paper sx={{boxShadow: "rgb(58, 53, 65, 0.1) 0px 2px 10px 0px", p: 2, backgroundColor: '#f7f7f7'}}>
              <Grid container item xs={12} sm={12} md={12} xl={12} lg={12} >
                <Grid  item xs={12} sm={12} md={3} xl={5} lg={5} justifyContent="space-between" sx={{display: 'flex'}}>
                  <Typography  component="h6" variant="h6" gutterBottom sx={{mb: 1, fontSize: 20, fontWeight: 500, color: '#f87207'}}>
                    Total - <CurrencyRupeeIcon sx={{verticalAlign: "sub",fontSize: '1.4rem'}} />{earningsData?.total} 
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={7} xl={7} lg={7} justifyContent={{xs: "flex-start",md: "flex-end"}} sx={{display: 'flex'}}>
                  <Stack direction={{xs: 'column', sm: 'row'}}>
                    <Box>
                      <LibrarySelect onLibrarySelected={onLibrarySelected} fullWidth={true}/>
                    </Box>
                    <Divider sx={{ height: 28, my: 0.5, mx: 1.5, display: {xs: 'none', sm: 'block'} }} orientation="vertical" />
                    <Box sx={{ mt: {xs: 2, sm: 0 }}}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <MobileDateRangePicker
                          className='date-range-with-no-edit-icon'
                          startText="From Date"
                          endText="To Date"
                          value={filterDates}
                          clearable={true}
                          maxDate={new Date()}
                          open={toggleDateRangePicker}
                          onClose={() => setToggleDateRangePicker(false)}
                          onChange={(newValue) => {
                            onDateSelected(newValue);
                          }}
                          onAccept={(data) => onDateAccepted(data)}
                          renderInput={(startProps, endProps) => (null)}
                        />
                      </LocalizationProvider>
                      <Button variant="text" color="typography" size="medium" startIcon={<DateRangeIcon />} onClick={()=> setToggleDateRangePicker(true)} sx={{textTransform: 'none', fontWeight: 400}}>
                        {getFormattedDate(filterDates)}
                      </Button>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
              <Grid container item xs={12} sm={12} md={12} xl={12} lg={12} sx={{mt: 6}} columnSpacing={2} rowSpacing={3}>

                <Grid item xs={12} sm={6} md={4} xl={2} lg={3} sx={{display: 'flex',}}  justifyContent="flex-start" alignItems="end">
                  <Box sx={{display: 'flex'}} >
                    <Avatar variant="square" sx={{ width: 46, height: 46, bgcolor: green[500], boxShadow: "rgba(58,53,65, 0.42) 0px 4px 8px -4px", borderRadius: '5px', mr: 1 }}>
                      <DateRangeIcon />
                    </Avatar>
                  </Box>
                  <Box sx={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column'}}>
                    <Typography component="span" variant="caption"  color="typography.heading" gutterBottom sx={{mb: 1, lineHeight: 'initial'}}>
                      Bookings
                    </Typography>
                    <Typography component="h6" variant="h6" color="typography.heading" gutterBottom sx={{mb: 0, lineHeight: 1.1}}>
                      <CurrencyRupeeIcon sx={{verticalAlign: "sub",fontSize: '1.3rem'}} />{earningsData?.bookingRevenue} 
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4} xl={2} lg={3} sx={{display: 'flex',}} justifyContent="flex-start" alignItems="end">
                  <Box sx={{display: 'flex'}} >
                    <Avatar variant="square" sx={{ width: 46, height: 46, bgcolor: yellow[800], boxShadow: "rgba(58,53,65, 0.42) 0px 4px 8px -4px", borderRadius: '5px', mr: 1 }}>
                      <AccountBalanceWalletIcon />
                    </Avatar>
                  </Box>
                  <Box sx={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column'}}>
                    <Typography component="span" variant="caption"  color="typography.heading" gutterBottom sx={{mb: 1, lineHeight: 'initial'}}>
                      Incentives
                    </Typography>
                    <Typography component="h6" variant="h6" color="typography.heading" gutterBottom sx={{mb: 0, lineHeight: 1.1}}>
                      <CurrencyRupeeIcon sx={{verticalAlign: "sub",fontSize: '1.3rem'}} />{earningsData?.incentives} 
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4} xl={2} lg={3} sx={{display: 'flex',}} justifyContent="flex-start" alignItems="end">
                  <Box sx={{display: 'flex'}} >
                    <Avatar variant="square" sx={{ width: 46, height: 46, bgcolor: red[700], boxShadow: "rgba(58,53,65, 0.42) 0px 4px 8px -4px", borderRadius: '5px', mr: 1 }}>
                      <DataUsageIcon />
                    </Avatar>
                  </Box>
                  <Box sx={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column'}}>
                    <Typography component="span" variant="caption"  color="typography.heading" gutterBottom sx={{mb: 1, lineHeight: 'initial'}}>
                      GST
                    </Typography>
                    <Typography component="h6" variant="h6" color="typography.heading" gutterBottom sx={{mb: 0, lineHeight: 1.1}}>
                      <CurrencyRupeeIcon sx={{verticalAlign: "sub",fontSize: '1.3rem'}} />{earningsData?.gst} 
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4} xl={2} lg={3} sx={{display: 'flex',}} justifyContent="flex-start" alignItems="end">
                  <Box sx={{display: 'flex'}} >
                    <Avatar variant="square" sx={{ width: 46, height: 46, bgcolor: blue[500], boxShadow: "rgba(58,53,65, 0.42) 0px 4px 8px -4px", borderRadius: '5px', mr: 1 }}>
                      <AccountBoxIcon />
                    </Avatar>
                  </Box>
                  <Box sx={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column'}}>
                    <Typography component="span" variant="caption"  color="typography.heading" gutterBottom sx={{mb: 1, lineHeight: 'initial'}}>
                      Gross owner share
                    </Typography>
                    <Typography component="h6" variant="h6" color="typography.heading" gutterBottom sx={{mb: 0, lineHeight: 1.1}}>
                      <CurrencyRupeeIcon sx={{verticalAlign: "sub",fontSize: '1.3rem'}} />{earningsData?.vendorShare} 
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4} xl={2} lg={3} sx={{display: 'flex',}} justifyContent="flex-start" alignItems="end">
                  <Box sx={{display: 'flex'}} >
                    <Avatar variant="square" sx={{ width: 46, height: 46, bgcolor: teal[500], boxShadow: "rgba(58,53,65, 0.42) 0px 4px 8px -4px", borderRadius: '5px', mr: 1 }}>
                      <BusinessIcon />
                    </Avatar>
                  </Box>
                  <Box sx={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column'}}>
                    <Typography component="span" variant="caption"  color="typography.heading" gutterBottom sx={{mb: 1, lineHeight: 'initial'}}>
                      Gross webook share
                    </Typography>
                    <Typography component="h6" variant="h6" color="typography.heading" gutterBottom sx={{mb: 0, lineHeight: 1.1}}>
                      <CurrencyRupeeIcon sx={{verticalAlign: "sub",fontSize: '1.3rem'}} />{earningsData?.webookShare} 
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4} xl={2} lg={3} sx={{display: 'flex',}} justifyContent="flex-start" alignItems="end">
                  <Box sx={{display: 'flex'}} >
                    <Avatar variant="square" sx={{ width: 46, height: 46, bgcolor: purple[300], boxShadow: "rgba(58,53,65, 0.42) 0px 4px 8px -4px", borderRadius: '5px', mr: 1 }}>
                      <ShoppingBagIcon />
                    </Avatar>
                  </Box>
                  <Box sx={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column'}}>
                    <Typography component="span" variant="caption"  color="typography.heading" gutterBottom sx={{mb: 1, lineHeight: 'initial'}}>
                      Expenses
                    </Typography>
                    <Typography component="h6" variant="h6" color="typography.heading" gutterBottom sx={{mb: 0, lineHeight: 1.1}}>
                      <CurrencyRupeeIcon sx={{verticalAlign: "sub",fontSize: '1.3rem'}} />{earningsData?.expenses} 
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4} xl={2} lg={3} sx={{display: 'flex',}} justifyContent="flex-start" alignItems="end">
                  <Box sx={{display: 'flex'}} >
                    <Avatar variant="square" sx={{ width: 46, height: 46, bgcolor: deepPurple[500], boxShadow: "rgba(58,53,65, 0.42) 0px 4px 8px -4px", borderRadius: '5px', mr: 1 }}>
                      <AccountBalanceIcon />
                    </Avatar>
                  </Box>
                  <Box sx={{display: 'flex', alignItems: 'flex-start', flexDirection: 'column'}}>
                    <Typography component="span" variant="caption"  color="typography.heading" gutterBottom sx={{mb: 1, lineHeight: 'initial'}}>
                      Amount paid to bak A/c
                    </Typography>
                    <Typography component="h6" variant="h6" color="typography.heading" gutterBottom sx={{mb: 0, lineHeight: 1.1}}>
                      <CurrencyRupeeIcon sx={{verticalAlign: "sub",fontSize: '1.3rem'}} />{earningsData?.payout} 
                    </Typography>
                  </Box>
                </Grid>

              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
 
export default React.memo(Earnings);
